import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Send, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { leadsApi, whatsappApi } from "@/lib/crmApi";

/**
 * Modal that picks a lead and sends the current payment simulation as a
 * formatted WhatsApp message. Used by SimulationPage's "Envoyer au lead"
 * button.
 *
 * The message is preformatted from the simulation context (apartment, mode,
 * échéancier rows). The user can still edit it before sending.
 */
export default function SendSimulationDialog({
  open,
  onOpenChange,
  apart,                    // {typo, unite, surface, prix, programme}
  mode,                     // {label, kind, rate_pct, downpayment_pct}
  durationYears,            // number | null (only for monthly modes)
  effectiveDownpayment,     // number — actual apport % used (>= mode.downpayment_pct)
  financing,                // {apport, capital, monthly, totalMensualites, coutTotal, interets, months, years, rate} | null
  rows,                     // [{etape, date, pct, montant, isMonthlySummary?, monthly?}]
  totalMontant,             // number
}) {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [leadId, setLeadId] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Re-fetch leads when the dialog opens. Cheap (single call) and avoids
  // showing stale data if the user added a lead in another tab.
  useEffect(() => {
    if (!open) return;
    setLoadingLeads(true);
    leadsApi
      .list()
      .then((data) => setLeads(Array.isArray(data) ? data : []))
      .catch(() => setLeads([]))
      .finally(() => setLoadingLeads(false));
    setSearch("");
    setLeadId("");
  }, [open]);

  // Build the default message from the simulation context whenever any of
  // the inputs change. Only re-init when the user hasn't typed anything.
  const defaultMessage = useMemo(
    () =>
      buildMessage({
        apart,
        mode,
        durationYears,
        effectiveDownpayment,
        financing,
        rows,
        totalMontant,
      }),
    [apart, mode, durationYears, effectiveDownpayment, financing, rows, totalMontant]
  );

  useEffect(() => {
    if (open) setMessage(defaultMessage);
  }, [open, defaultMessage]);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    const qd = q.replace(/[^0-9]/g, "");
    return leads.filter(
      (l) =>
        (l.nom || "").toLowerCase().includes(q) ||
        (qd && (l.telephone || "").includes(qd)) ||
        (l.programme_immobilier || "").toLowerCase().includes(q)
    );
  }, [leads, search]);

  const selectedLead = leads.find((l) => l.id === leadId) || null;

  const send = async () => {
    if (!selectedLead) {
      toast.error("Choisis un lead");
      return;
    }
    if (!selectedLead.telephone || !selectedLead.telephone.trim()) {
      toast.error("Ce lead n'a pas de numéro de téléphone");
      return;
    }
    if (!message.trim()) {
      toast.error("Le message est vide");
      return;
    }
    setSending(true);
    try {
      await whatsappApi.send({
        phone: selectedLead.telephone,
        text: message,
        lead_id: selectedLead.id,
      });
      toast.success(`Simulation envoyée à ${selectedLead.nom} sur WhatsApp`);
      onOpenChange(false);
    } catch (e) {
      const detail =
        e?.response?.data?.detail?.message ||
        e?.response?.data?.detail ||
        e?.humanMessage ||
        "Échec de l'envoi WhatsApp";
      toast.error(typeof detail === "string" ? detail : "Échec de l'envoi WhatsApp");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Envoyer la simulation au lead</DialogTitle>
          <DialogDescription>
            Le message ci-dessous part directement par WhatsApp. Tu peux l'éditer
            avant l'envoi.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Lead picker */}
          <div className="space-y-1.5">
            <Label className="text-xs">Lead destinataire</Label>
            {selectedLead ? (
              <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 dark:border-emerald-900/40 dark:bg-emerald-950/30">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{selectedLead.nom}</p>
                  <p className="flex items-center gap-1.5 truncate text-[11px] text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {selectedLead.telephone || "(pas de téléphone)"}
                    {selectedLead.programme_immobilier
                      ? ` · ${selectedLead.programme_immobilier}`
                      : ""}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLeadId("")}
                  className="text-xs"
                >
                  Changer
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher par nom, téléphone, programme…"
                    className="pl-9"
                    data-testid="send-simu-search"
                  />
                </div>
                <ul className="max-h-44 space-y-0.5 overflow-y-auto rounded-lg border border-border/60 bg-muted/20 p-1">
                  {loadingLeads ? (
                    <li className="p-2 text-xs italic text-muted-foreground">
                      Chargement des leads…
                    </li>
                  ) : filteredLeads.length === 0 ? (
                    <li className="p-2 text-xs italic text-muted-foreground">
                      Aucun lead trouvé.
                    </li>
                  ) : (
                    filteredLeads.slice(0, 50).map((l) => (
                      <li key={l.id}>
                        <button
                          type="button"
                          onClick={() => setLeadId(l.id)}
                          className="w-full rounded p-1.5 text-left text-sm hover:bg-muted/60"
                          data-testid={`send-simu-lead-${l.id}`}
                          disabled={!l.telephone}
                        >
                          <div className="truncate font-medium">{l.nom}</div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {l.telephone || "(pas de téléphone)"}
                            {l.programme_immobilier && ` · ${l.programme_immobilier}`}
                          </div>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </>
            )}
          </div>

          {/* Message preview / editor */}
          <div className="space-y-1.5">
            <Label className="text-xs">Message WhatsApp</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              className="font-mono text-[11px]"
              data-testid="send-simu-message"
            />
            <p className="text-[10px] text-muted-foreground">
              {message.length} caractères ·{" "}
              <button
                type="button"
                onClick={() => setMessage(defaultMessage)}
                className="text-emerald-600 hover:underline"
              >
                Restaurer le message par défaut
              </button>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
            Annuler
          </Button>
          <Button
            onClick={send}
            disabled={sending || !selectedLead}
            className="gap-1.5"
            data-testid="send-simu-confirm"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Envoi…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Envoyer sur WhatsApp
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Helpers ---------------------------------------------------------------
const fmtFCFA = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Math.round(n || 0)
  ) + " FCFA";

function buildMessage({
  apart,
  mode,
  durationYears,
  effectiveDownpayment,
  financing,
  rows,
  totalMontant,
}) {
  if (!apart || !mode) return "";
  const lines = [];
  lines.push("Bonjour 👋");
  lines.push("");
  lines.push(
    `Voici la simulation détaillée pour l'appartement *${apart.typo} · ${apart.unite}* (${apart.surface} m²)${
      apart.programme ? ` du programme *${apart.programme}*` : ""
    } :`
  );
  lines.push("");
  lines.push(`💰 Prix de vente : *${fmtFCFA(apart.prix)}*`);
  lines.push(`💳 Mode de paiement : *${mode.label}*`);

  // Monthly modes (Location-Vente): show the full financing breakdown so the
  // lead understands EXACTLY what they pay (apport + loan with interest).
  if (mode.kind === "monthly" && financing) {
    lines.push("");
    lines.push("📊 *Détail du financement*");
    lines.push(
      `🏦 Apport (à la signature) : *${fmtFCFA(financing.apport)}* (${effectiveDownpayment.toFixed(1)}%)`
    );
    lines.push(`🏛️ Capital emprunté : *${fmtFCFA(financing.capital)}*`);
    lines.push(
      `📈 Taux annuel : *${financing.rate}%* — Durée : *${financing.years} an${
        financing.years > 1 ? "s" : ""
      }* (${financing.months} mois)`
    );
    lines.push(`💵 Mensualité : *${fmtFCFA(financing.monthly)}* / mois`);
    lines.push(`➕ Total des mensualités : *${fmtFCFA(financing.totalMensualites)}*`);
    lines.push(`💸 Intérêts payés : *${fmtFCFA(financing.interets)}*`);
    lines.push(`✅ *Coût total : ${fmtFCFA(financing.coutTotal)}*`);
  } else if (mode.kind === "monthly") {
    // Fallback if financing wasn't computed (older callers).
    if (durationYears) lines.push(`📅 Durée : *${durationYears} an${durationYears > 1 ? "s" : ""}*`);
    if (mode.rate_pct) lines.push(`📈 Taux annuel : *${mode.rate_pct}%*`);
    const dp = Number(effectiveDownpayment ?? mode.downpayment_pct ?? 0);
    if (dp)
      lines.push(`🏦 Apport initial : *${dp}%* (${fmtFCFA((apart.prix * dp) / 100)})`);
  }

  lines.push("");
  lines.push("📅 *Échéancier*");
  (rows || []).forEach((r) => {
    if (r.isMonthlySummary) {
      lines.push(
        `• ${r.etape} (${r.date}) → *${fmtFCFA(r.montant)}* (${fmtFCFA(r.monthly)} / mois)`
      );
    } else {
      lines.push(
        `• ${r.etape}${r.date && r.date !== "—" ? ` — ${r.date}` : ""} → *${fmtFCFA(r.montant)}*`
      );
    }
  });

  // For Comptant (schedule) modes: show the simple total. For Location-Vente,
  // the "Coût total" was already shown in the financing breakdown above.
  if (mode.kind !== "monthly") {
    lines.push("");
    lines.push(`Total : *${fmtFCFA(totalMontant)}*`);
  }

  lines.push("");
  lines.push("N'hésitez pas si vous avez des questions ou souhaitez fixer un RDV.");
  lines.push("");
  lines.push("L'équipe SOGI 🏢");
  return lines.join("\n");
}
